import { ReactNode, ChangeEvent } from "react"
import { css, cx } from "@emotion/css"
import { useField } from "formik"

import TextField, { TextFieldProps } from "@material-ui/core/TextField"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import ListSubheader from "@material-ui/core/ListSubheader"
import MenuItem from "@material-ui/core/MenuItem"
import { Option } from "components/types"
import { useI18n } from "providers/I18n"

export type OptionsProps = {
  error?: string | undefined
  name: string
  options: (Option & { disabled?: boolean })[] | Record<string, Option[]>
  displayEmpty?: boolean
  renderValue?: (value: any) => ReactNode
  paperClass?: string
  rootClass?: string
  inputClass?: string
  inputLabelClass?: string
  selectClass?: string
  iconClass?: string
} & Omit<TextFieldProps, "error" | "variant">

const ITEM_HEIGHT = 36

const styles = {
  subheader: css`
    background-color: var(--white, #fff);
  `,
  input: css`
    background-color: var(--white, #fff);
  `,
  item: css`
    font-size: 13px;
  `,
}

export function Options(props: OptionsProps) {
  const {
    value,
    name,
    error,
    fullWidth = true,
    helperText,
    options,
    displayEmpty,
    renderValue,
    size = "small",
    margin = "none",
    paperClass,
    rootClass,
    inputClass,
    inputLabelClass,
    selectClass,
    iconClass,
    ...rest
  } = props
  const { t } = useI18n()
  const hasError = !!error
  const helper = hasError ? t(error!) : helperText

  return (
    <TextField
      fullWidth={fullWidth}
      variant="outlined"
      size={size}
      margin={margin}
      {...rest}
      select
      error={hasError}
      helperText={helper}
      name={name}
      value={value || ""}
      InputLabelProps={{
        ...rest.InputLabelProps,
        shrink: !!value || undefined,
        classes: { root: inputLabelClass },
      }}
      InputProps={{
        classes: {
          root: cx(styles.input, rootClass),
          notchedOutline: inputClass,
        },
      }}
      SelectProps={{
        classes: {
          root: selectClass,
          icon: iconClass,
        },
        IconComponent: DropdownIcon,
        displayEmpty,
        renderValue,

        MenuProps: {
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          getContentAnchorEl: null,
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 10 + ITEM_HEIGHT / 2,
              marginTop: "var(--spacing-m)",
            },
            className: paperClass,
          },
        },
      }}
    >
      {Array.isArray(options)
        ? options.map((option, idx) => (
            <MenuItem
              data-testid={`input-options-${idx}`}
              key={`${option.value}-${idx}`}
              value={option.value}
              className={styles.item}
              disabled={option?.disabled}
            >
              {option.label}
            </MenuItem>
          ))
        : Object.entries(options)
            .map(([groupName, groupOptions]) => {
              return [
                <ListSubheader className={styles.subheader} key={groupName}>
                  {groupName}
                </ListSubheader>,
                ...groupOptions.map((option, idx) => (
                  <MenuItem
                    key={`${option.value}-${idx}`}
                    value={option.value}
                    className={styles.item}
                  >
                    {option.label}
                  </MenuItem>
                )),
              ]
            })
            .flat()}
    </TextField>
  )
}

export default Options

type PropsHandledByFormik = "value" | "error"
type AdditionalProps = {
  name: string
  onChange?(e: ChangeEvent<any>): void
}
export type OptionsFormikProps = Omit<OptionsProps, PropsHandledByFormik> &
  AdditionalProps

export function OptionsFormik(props: OptionsFormikProps) {
  const { name, defaultValue, onChange: customOnChange, ...rest } = props
  const [{ value, onChange, ...field }, { error, touched }] =
    useField<string>(name)
  const hasError = Boolean(touched && error)
  const errorMessage = hasError ? error : undefined

  const v = value ?? defaultValue ?? ""

  return (
    <Options
      {...rest}
      value={v}
      error={errorMessage}
      {...field}
      onChange={(event) => {
        onChange(event)
        customOnChange && customOnChange(event)
      }}
    />
  )
}
